import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import { GetSettingDocument, SetSettingDocument, type SettingValue } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * useUserSetting — read/write ONE value in the generic user-scoped settings bag
 * (the workspaceId-NULL `setting` row, so the value follows the user across
 * workspaces). Returns the parsed current value plus a setter that persists via
 * `SetSetting` AND writes the result back into the `GetSetting` cache. That
 * cache write is the point: without it a later cache-first read — a remount, or
 * a second consumer of the same key — sees the stale pre-write value and
 * silently reverts what was just saved.
 *
 * `parse` validates the loose JSON payload into `T`; pass a STABLE (module-level)
 * normalizer so the derived value isn't recomputed every render.
 *
 * This is the simple key/value case. Anything needing a localStorage mirror,
 * debounced writes, or session-wins-until-reload semantics uses a purpose-built
 * hook instead (see use-table-view-settings).
 */
export function useUserSetting<T>(
  key: string,
  parse: (value: unknown) => T,
  opts?: {
    /** Skip the read entirely (a consumer that only conditionally uses the
     *  setting — e.g. the widget store outside `personal` mode — shouldn't
     *  fire a query per view). `value` is `parse(null)` while skipped. */
    skip?: boolean;
  },
): { value: T; setValue: (next: T) => void; initialLoading: boolean; loaded: boolean } {
  const variables = useMemo(() => ({ key }), [key]);
  const { data, loading } = useQuery(GetSettingDocument, {
    variables,
    fetchPolicy: "cache-first",
    skip: opts?.skip ?? false,
  });
  const value = useMemo(
    () => parse(data?.setting ?? null),
    [data?.setting, parse],
  );

  const [setSetting] = useMutation(SetSettingDocument);
  const setValue = useCallback(
    (next: T) => {
      setSetting({
        variables: {
          key,
          value: next as unknown as Record<string, never>,
        },
        optimisticResponse: { setSetting: true },
        // The setting query has no normalized entity, so write it explicitly to
        // keep a later cache read consistent with what was saved.
        update: (cache) => {
          cache.writeQuery({
            query: GetSettingDocument,
            variables: { key },
            // `Exclude<…, undefined>`: the write always SETS the key, so the
            // cached query is a real answer rather than an absent field.
            data: { setting: next as Exclude<SettingValue, undefined> },
          });
        },
        onError: (err) => toastMutationError(err, "Failed to save setting"),
      });
    },
    [setSetting, key],
  );

  return { value, setValue, initialLoading: loading && !data, loaded: data !== undefined };
}
